<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { CAMPAIGN_AUDIENCE_TYPES } from 'shared/constants/campaign.js';
import { buildCampaignAudience } from 'shared/helpers/campaignHelper';
import { downloadBlob } from 'dashboard/helper/downloadHelper';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { serverErrorMessage } from 'dashboard/store/utils/api';

const props = defineProps({
  audienceType: {
    type: String,
    default: CAMPAIGN_AUDIENCE_TYPES.LABEL,
  },
  selectedLabels: {
    type: Array,
    default: () => [],
  },
  selectedInboxId: {
    type: [Number, String],
    default: null,
  },
  uploadedContactIds: {
    type: Array,
    default: () => [],
  },
  // The custom attribute columns the uploaded spreadsheet brought, as the server filed them.
  uploadedColumns: {
    type: Array,
    default: () => [],
  },
  // The columns the campaign's message reads, after phone and name: { name, example, description,
  // variable }. They go into the Excel to fill in, and an upload without one of them is pointed out
  // here rather than as a preview full of empty variables.
  spreadsheetColumns: {
    type: Array,
    default: () => [],
  },
  templateFileName: {
    type: String,
    default: 'campaign-audience.xlsx',
  },
  labelOptions: {
    type: Array,
    default: () => [],
  },
  inboxOptions: {
    type: Array,
    default: () => [],
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:audienceType',
  'update:selectedLabels',
  'update:selectedInboxId',
  'update:uploadedContactIds',
  'update:uploadedColumns',
]);

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isUploading = computed(() => uiFlags.value.isUploadingAudience);

const typeOptions = computed(() => [
  {
    value: CAMPAIGN_AUDIENCE_TYPES.LABEL,
    label: t('CAMPAIGN.AUDIENCE_TYPE.LABEL'),
  },
  {
    value: CAMPAIGN_AUDIENCE_TYPES.CONVERSATION_LABEL,
    label: t('CAMPAIGN.AUDIENCE_TYPE.CONVERSATION_LABEL'),
  },
  {
    value: CAMPAIGN_AUDIENCE_TYPES.ALL_CONTACTS,
    label: t('CAMPAIGN.AUDIENCE_TYPE.ALL_CONTACTS'),
  },
  {
    value: CAMPAIGN_AUDIENCE_TYPES.INBOX,
    label: t('CAMPAIGN.AUDIENCE_TYPE.INBOX'),
  },
  {
    value: CAMPAIGN_AUDIENCE_TYPES.UPLOAD,
    label: t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD'),
  },
]);

// ComboBox clears the selection when the already-selected option is clicked
// again; the audience type must always have one of the four values, so
// ignore that empty toggle-off instead of forwarding it.
const handleTypeChange = value => {
  if (value) emit('update:audienceType', value);
};

// "Why is my campaign empty?" should be answered here, not after creating it: a label used on
// website conversations matches people with no phone, and the count says so.
const preview = ref(null);
let previewTimer = null;

const audienceForPreview = () => {
  // An upload already reports how many rows came in, so only the audiences the person picks by
  // hand get a count.
  if (props.audienceType === CAMPAIGN_AUDIENCE_TYPES.UPLOAD) return null;

  const audience = buildCampaignAudience({
    audienceType: props.audienceType,
    selectedLabels: props.selectedLabels,
    inboxId: props.selectedInboxId,
  });
  return audience.length ? audience : null;
};

const fetchPreview = async () => {
  const audience = audienceForPreview();
  if (!audience) {
    preview.value = null;
    return;
  }
  try {
    const { data } = await CampaignsAPI.audiencePreview(audience);
    preview.value = data;
  } catch (error) {
    preview.value = null;
  }
};

watch(
  () => [props.audienceType, props.selectedLabels, props.selectedInboxId],
  () => {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(fetchPreview, 400);
  },
  { deep: true, immediate: true }
);

const fileInput = ref(null);
const selectedFileName = ref('');
const uploadSummary = ref(null);
const uploadError = ref('');

const isDownloading = ref(false);

// The spreadsheet as it will look: what each column is for, and the template's example for it.
const tableColumns = computed(() => [
  {
    name: 'phone_number',
    hint: t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_REQUIRED'),
  },
  { name: 'name', hint: t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_OPTIONAL') },
  ...props.spreadsheetColumns,
]);

const missingColumns = computed(() =>
  uploadSummary.value
    ? props.spreadsheetColumns
        .map(column => column.name)
        .filter(name => !props.uploadedColumns.includes(name))
    : []
);

const handleFileClick = () => fileInput.value?.click();

const downloadTemplate = async () => {
  isDownloading.value = true;
  try {
    const { data } = await CampaignsAPI.audienceTemplate(
      props.spreadsheetColumns.map(({ name, example, description }) => ({
        name,
        example,
        description,
      }))
    );
    downloadBlob(props.templateFileName, data);
  } catch (error) {
    useAlert(t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_SAMPLE_ERROR'));
  } finally {
    isDownloading.value = false;
  }
};

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);

  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const handleFileChange = async () => {
  const file = fileInput.value?.files[0];
  if (!file) return;

  selectedFileName.value = processFileName(file.name);
  uploadError.value = '';
  uploadSummary.value = null;

  try {
    const response = await store.dispatch('campaigns/uploadAudience', file);
    emit('update:uploadedContactIds', response.contact_ids);
    emit('update:uploadedColumns', response.columns);
    uploadSummary.value = {
      count: response.count,
      createdCount: response.created_count,
      skippedCount: response.skipped_count,
    };
  } catch (error) {
    // The server says what to fix: an .xls to save as .xlsx, too many rows.
    uploadError.value =
      serverErrorMessage(error) || t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_ERROR');
    emit('update:uploadedContactIds', []);
    emit('update:uploadedColumns', []);
  }
};

const handleRemoveFile = () => {
  selectedFileName.value = '';
  uploadSummary.value = null;
  uploadError.value = '';
  emit('update:uploadedContactIds', []);
  emit('update:uploadedColumns', []);
  if (fileInput.value) {
    fileInput.value.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <ComboBox
      :model-value="audienceType"
      :options="typeOptions"
      class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      @update:model-value="handleTypeChange"
    />

    <TagMultiSelectComboBox
      v-if="
        audienceType === CAMPAIGN_AUDIENCE_TYPES.LABEL ||
        audienceType === CAMPAIGN_AUDIENCE_TYPES.CONVERSATION_LABEL
      "
      :model-value="selectedLabels"
      :options="labelOptions"
      :placeholder="t('CAMPAIGN.AUDIENCE_TYPE.LABEL_PLACEHOLDER')"
      :has-error="hasError"
      :message="errorMessage"
      class="[&>div>button]:bg-n-alpha-black2"
      @update:model-value="emit('update:selectedLabels', $event)"
    />

    <ComboBox
      v-else-if="audienceType === CAMPAIGN_AUDIENCE_TYPES.INBOX"
      :model-value="selectedInboxId"
      :options="inboxOptions"
      :has-error="hasError"
      :placeholder="t('CAMPAIGN.AUDIENCE_TYPE.INBOX_PLACEHOLDER')"
      :message="errorMessage"
      class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      @update:model-value="emit('update:selectedInboxId', $event)"
    />

    <p
      v-if="preview"
      class="text-sm"
      :class="preview.count ? 'text-n-slate-11' : 'text-n-amber-11'"
    >
      {{
        preview.without_phone
          ? t('CAMPAIGN.AUDIENCE_TYPE.PREVIEW_WITH_SKIPPED', {
              count: preview.count,
              skipped: preview.without_phone,
            })
          : t('CAMPAIGN.AUDIENCE_TYPE.PREVIEW', { count: preview.count })
      }}
    </p>

    <p
      v-else-if="audienceType === CAMPAIGN_AUDIENCE_TYPES.ALL_CONTACTS"
      class="mb-0 text-xs text-n-slate-11"
    >
      {{ t('CAMPAIGN.AUDIENCE_TYPE.ALL_CONTACTS_DESCRIPTION') }}
    </p>

    <div
      v-else-if="audienceType === CAMPAIGN_AUDIENCE_TYPES.UPLOAD"
      class="flex flex-col gap-3 p-3 rounded-lg bg-n-alpha-black2"
    >
      <p class="mb-0 text-sm text-n-slate-12">
        {{ t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_DESCRIPTION') }}
      </p>
      <div class="overflow-x-auto border rounded-lg border-n-weak bg-n-solid-1">
        <table class="text-xs">
          <thead>
            <tr>
              <th
                v-for="column in tableColumns"
                :key="column.name"
                class="px-3 py-2 font-mono font-medium text-left border-b whitespace-nowrap border-n-weak text-n-slate-12"
              >
                {{ column.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                v-for="column in tableColumns"
                :key="column.name"
                class="px-3 py-2 align-top whitespace-nowrap text-n-slate-11"
              >
                <span
                  v-if="column.variable"
                  class="px-1 rounded bg-n-blue-3 text-n-blue-11"
                >
                  {{ column.variable }}
                </span>
                <span v-else>{{ column.hint }}</span>
                <span v-if="column.example" class="block mt-1">
                  {{
                    t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_EXAMPLE', {
                      value: column.example,
                    })
                  }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_PHONE_TIP') }}
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          :label="t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_SAMPLE_CSV')"
          icon="i-lucide-download"
          color="slate"
          variant="faded"
          size="sm"
          type="button"
          :is-loading="isDownloading"
          @click="downloadTemplate"
        />
        <span v-if="selectedFileName" class="text-sm text-n-slate-12">
          {{ selectedFileName }}
        </span>
        <Button
          v-if="!selectedFileName"
          :label="t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_CHOOSE_FILE')"
          icon="i-lucide-upload"
          size="sm"
          type="button"
          :is-loading="isUploading"
          @click="handleFileClick"
        />
        <div v-else class="flex items-center gap-1">
          <Button
            :label="t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_CHANGE_FILE')"
            color="slate"
            variant="ghost"
            size="sm"
            type="button"
            :is-loading="isUploading"
            @click="handleFileClick"
          />
          <div class="w-px h-3 bg-n-strong" />
          <Button
            icon="i-lucide-trash"
            color="slate"
            variant="ghost"
            size="sm"
            type="button"
            @click="handleRemoveFile"
          />
        </div>
      </div>
      <p v-if="uploadSummary" class="mb-0 text-xs text-n-teal-11">
        {{
          t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_SUMMARY', {
            count: uploadSummary.count,
            createdCount: uploadSummary.createdCount,
          })
        }}
        <template v-if="uploadedColumns.length">
          ·
          {{
            t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_COLUMNS_READ', {
              columns: uploadedColumns.join(', '),
            })
          }}
        </template>
      </p>
      <p
        v-if="uploadSummary?.skippedCount"
        class="mb-0 text-xs text-n-amber-11"
      >
        {{
          t(
            'CAMPAIGN.AUDIENCE_TYPE.UPLOAD_SKIPPED',
            { count: uploadSummary.skippedCount },
            uploadSummary.skippedCount
          )
        }}
      </p>
      <p v-if="missingColumns.length" class="mb-0 text-xs text-n-amber-11">
        {{
          t('CAMPAIGN.AUDIENCE_TYPE.UPLOAD_MISSING_COLUMNS', {
            columns: missingColumns.join(', '),
          })
        }}
      </p>
      <p
        v-if="uploadError || (hasError && !uploadedContactIds.length)"
        class="mb-0 text-xs text-n-ruby-9"
      >
        {{ uploadError || errorMessage }}
      </p>
      <input
        ref="fileInput"
        type="file"
        accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        class="hidden"
        @change="handleFileChange"
      />
    </div>
  </div>
</template>
